/**
 * 과태료 원장 API — 산업안전보건법 §36 위험성평가 과태료 (2027/2028 시행)
 *
 * 법률 제21374호(2026.2.19) 부칙 제1조:
 *   · 상시 50명 이상: 2027-01-01 시행
 *   · 상시 50명 미만: 2028-01-01 시행
 *
 * 판정 근거는 사업장이 **이미 보유한 기록**(위험성평가·개선대책 이행·TBM)뿐이다.
 * 사용량 차감 없음(LLM 미사용).
 */
import { api } from './api'

export type PenaltyStatus = 'ok' | 'partial' | 'missing' | 'overdue'

export interface PenaltyItem {
  code: string
  title: string
  /** 근거 조문 — 예: 산업안전보건법 제36조제1항 */
  article: string
  /** 과태료 조문 — 예: 제175조제4항제2호의2 */
  penalty_article: string
  summary: string
  /** 법정 상한(원) */
  penalty_krw: number
  status: PenaltyStatus
  /** 현재 열려 있는 노출액(충족이면 0) */
  exposure_krw: number
  /** 왜 미충족인지 */
  reason?: string | null
  /** 충족이지만 위험 신호(형식적 실시 등) — 노출액은 0이나 경고 표시 */
  warning?: string | null
  detail: {
    ra_completed: number
    /** 참여자 명단이 있는 완료 평가 수 */
    ra_participated: number
    /** 안전소통 제보가 반영된 완료 평가 수 (익명 제보 포함 — §36②③ 참여 증빙) */
    ra_from_reports: number
    /** 최근 180일 안전소통 제보 수 (아직 평가에 반영 안 됐을 수 있음) */
    reports_total: number
    measures_total: number
    measures_done: number
    tbm_recent: number
    earliest_due?: string | null
  }
  /** 해결하러 갈 탭 */
  deeplink_tab: string
  cta: string
}

export interface PenaltyExposure {
  site_id: string
  workers: number
  /** 근로자 수 미입력 → 시행일 확정 불가(보수적으로 2027 적용) */
  workers_unknown: boolean
  effective_date: string
  d_day: number
  in_force: boolean
  total_penalty_krw: number
  exposed_krw: number
  items: PenaltyItem[]
  disclaimer: string
  evaluated_at: string
}

export const penaltyApi = {
  /** 우리 사업장 §36 과태료 노출 진단 (차감 없음) */
  exposure: (siteId: string) =>
    api.get<PenaltyExposure>(`/sites/${siteId}/penalty-exposure`).then(r => r.data),

  /**
   * 근로감독 대비 증빙 패키지 PDF (P5).
   * 기존 '바인더'는 증빙 건수만 적힌 목차 XLSX 였다 — 감독관에게 목차를 낼 수는 없다.
   * 이건 위험성평가 원본 전문까지 실제로 병합한 단일 제출용 PDF다. 유료 전용(증빙 게이트).
   */
  inspectionPackage: (siteId: string) =>
    api.get(`/sites/${siteId}/inspection-package`, {
      responseType: 'blob', timeout: 180_000,
    }).then(r => r.data as Blob),

  /** 패키지에 담을 수 있는 기록 목록 — 사업주가 고를 수 있어야 한다 */
  packageRecords: (siteId: string) =>
    api.get<PackageRecords>(`/sites/${siteId}/inspection-package/records`).then(r => r.data),

  /** 고른 기록만 담아 패키지 만들기 (카테고리를 비우면 그 카테고리는 전체) */
  inspectionPackageSelected: (siteId: string, selected: PackageSelection) =>
    api.post(`/sites/${siteId}/inspection-package`, selected, {
      responseType: 'blob', timeout: 180_000,
    }).then(r => r.data as Blob),
}

export interface PackageRecord {
  id: string
  title: string
  date: string
  note?: string
}
export interface PackageRecords {
  risk_assessments: PackageRecord[]
  tbms: PackageRecord[]
  work_permits: PackageRecord[]
  accidents: PackageRecord[]
  reports: PackageRecord[]
}
export type PackageCategory = keyof PackageRecords
export type PackageSelection = Partial<Record<PackageCategory, string[]>>
